import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { renderToStaticMarkup, renderToString } from "react-dom/server";
import { clusterMarkerSizes, NaverClusterMarker } from "../../../../components/StoreLocator/components/NaverClusterMarker";
import { StoreDetailsPopup } from "../../../../components/StoreLocator/components/StoreDetailsPopup";
import { UserMarkerInfoWindow } from "../../../../components/StoreLocator/components/UserMarkerInfoWindow";
import ICONS from "../../../../components/StoreLocator/store-locator-icons";
import { getNaverDirectionUrl, getStoreLocatorMarkerIcon } from "../../../../components/StoreLocator/utils";
import { useConfig } from "../../../../providers/useConfig";
import { handleExtendNaverMaps, MarkerClustering } from "./MarkerClustering";
const defaultMarkerIcon = getStoreLocatorMarkerIcon(ICONS.DEFAULT_MARKER_NAVER);
const activeMarkerIcon = getStoreLocatorMarkerIcon(ICONS.ACTIVE_MARKER);
export const useNaverMapsStoreLocator = ({ defaultLat = "0", defaultLng = "0", handleSelectStore, activeStoreId, defaultZoom = 10, storeDetailsPage, isSearchVisible, handleLoadFlotiqStores, }) => {
    const { mapProviderExternalJs } = useConfig();
    const [isMapLoading, setIsMapLoading] = useState(false);
    const [isMapError, setIsMapError] = useState(false);
    const [isSearchResultPending, setIsSearchResultPending] = useState(false);
    const markers = useRef([]);
    const mapRef = useRef(null);
    const inputRef = useRef(null);
    const naverMapRef = useRef(null);
    const infoWindowRef = useRef(null);
    const userMarkerInfoWindow = useRef(null);
    const markerClusters = useRef(null);
    const deleteAllMarkers = () => {
        markers.current.forEach((marker) => {
            marker.setMap(null);
        });
        markers.current = [];
        markerClusters.current = null;
        infoWindowRef.current?.close();
        handleSelectStore("");
    };
    const setDefaultIconForMarkers = () => {
        markers.current.forEach((marker) => {
            marker.setIcon(defaultMarkerIcon);
        });
    };
    const handleMarkerClick = (store, marker) => () => {
        naverMapRef.current?.setCenter(marker.getPosition());
        handleSelectStore(store.id);
        const storePopupHtml = renderToStaticMarkup(_jsx(StoreDetailsPopup, { store: store, directionUrl: getNaverDirectionUrl(store.ExternalId), storeDetailsPage: storeDetailsPage }));
        infoWindowRef.current = new naver.maps.InfoWindow({
            content: storePopupHtml,
            borderWidth: 0,
            disableAnchor: true,
            backgroundColor: "transparent",
        });
        if (infoWindowRef.current.getMap()) {
            infoWindowRef.current.close();
        }
        else {
            infoWindowRef.current.open(naverMapRef.current, marker);
        }
        setDefaultIconForMarkers();
        marker.setIcon(activeMarkerIcon);
    };
    const handleLoadMarkers = (stores) => {
        if (!naverMapRef.current) {
            return;
        }
        deleteAllMarkers();
        stores.forEach((store) => {
            const marker = new naver.maps.Marker({
                position: new naver.maps.LatLng(store.Location.lat, store.Location.lon),
                map: naverMapRef.current,
                title: store.id,
                icon: defaultMarkerIcon,
            });
            markers.current = [...markers.current, marker];
            naver.maps.Event.addListener(marker, "click", handleMarkerClick(store, marker));
        });
        const clusterMarkers = clusterMarkerSizes.map((size) => {
            return {
                content: renderToString(_jsx(NaverClusterMarker, { type: size })),
                size: new naver.maps.Size(40, 40),
                anchor: new naver.maps.Point(20, 20),
            };
        });
        const clusters = new MarkerClustering({
            minClusterSize: 2,
            maxZoom: 13,
            map: naverMapRef.current,
            markers: markers.current,
            disableClickZoom: false,
            gridSize: 120,
            icons: clusterMarkers,
            indexGenerator: [10, 100, 200, 500, 1000],
            stylingFunction: function (clusterMarker, count) {
                clusterMarker.eventTarget.innerText = count;
            },
        });
        markerClusters.current = clusters;
    };
    const handleMapEvent = useCallback(() => {
        setDefaultIconForMarkers();
        handleLoadFlotiqStores(naverMapRef.current);
    }, [handleLoadFlotiqStores]);
    const handleCreateMap = useCallback(() => {
        handleExtendNaverMaps(naver);
        const lat = Number(defaultLat);
        const lng = Number(defaultLng);
        naverMapRef.current = new naver.maps.Map("storeLocatorMap", {
            center: new naver.maps.LatLng(lat, lng),
            zoom: defaultZoom,
            zoomControl: true,
        });
        naverMapRef?.current?.addListener("click", () => {
            if (infoWindowRef.current?.getMap()) {
                infoWindowRef.current?.close();
            }
            if (userMarkerInfoWindow.current?.getMap()) {
                userMarkerInfoWindow.current?.close();
            }
            setDefaultIconForMarkers();
            handleSelectStore("");
        });
        naverMapRef?.current?.addListener("zoom_changed", () => {
            handleMapEvent();
        });
        naverMapRef?.current?.addListener("resize", () => {
            handleMapEvent();
        });
        naverMapRef.current?.addListener("dragend", () => {
            handleMapEvent();
        });
        handleLoadFlotiqStores(naverMapRef.current);
    }, [defaultLat, defaultLng, defaultZoom, handleLoadFlotiqStores, handleMapEvent, handleSelectStore]);
    const handleInitMap = useCallback(() => {
        setIsMapLoading(true);
        const head = document.head;
        const script = document.createElement("script");
        script.src = mapProviderExternalJs;
        script.type = "text/javascript";
        script.onload = () => {
            handleCreateMap();
            setIsMapLoading(false);
        };
        script.onerror = () => {
            setIsMapError(true);
            setIsMapLoading(false);
        };
        head.appendChild(script);
    }, [handleCreateMap, mapProviderExternalJs]);
    const handleLoadUserMarker = (userLat, userLng) => {
        if (naverMapRef.current) {
            naverMapRef.current.setCenter(new naver.maps.LatLng(userLat, userLng));
            naverMapRef.current.setZoom(15);
            const infoWindowContent = renderToStaticMarkup(_jsx(UserMarkerInfoWindow, {}));
            userMarkerInfoWindow.current = new naver.maps.InfoWindow({
                content: infoWindowContent,
            });
            const userMarker = new naver.maps.Marker({
                position: new naver.maps.LatLng(userLat, userLng),
                map: naverMapRef.current,
                icon: {
                    url: "data:image/svg+xml;charset=utf-8," + encodeURIComponent(ICONS.USER_LOCATION),
                    scaledSize: new naver.maps.Size(30, 30),
                },
            });
            userMarker.addListener("click", () => {
                if (userMarkerInfoWindow.current.getMap()) {
                    userMarkerInfoWindow.current.close();
                }
                else {
                    userMarkerInfoWindow.current.open(naverMapRef.current, userMarker);
                }
            });
            userMarker.trigger("click");
        }
    };
    const handleInputSearchClick = () => {
        setIsSearchResultPending(true);
        if (inputRef.current && naver.maps?.Service?.geocode) {
            const searchValue = inputRef.current.value;
            if (searchValue) {
                naver.maps.Service.geocode({ query: searchValue }, (status, response) => {
                    if (status === naver.maps.Service.Status.ERROR) {
                        console.error("Something went wrong. Please try again.");
                        return;
                    }
                    const { v2 } = response;
                    if (v2) {
                        const { addresses } = v2;
                        if (addresses.length > 0) {
                            const { x, y } = addresses[0];
                            naverMapRef.current?.setCenter(new naver.maps.LatLng(Number(y), Number(x)));
                            naverMapRef.current?.setZoom(10);
                        }
                    }
                    setIsSearchResultPending(false);
                });
            }
        }
    };
    useEffect(() => {
        handleInitMap();
    }, [handleInitMap]);
    useEffect(() => {
        if (activeStoreId) {
            const activeStore = markers.current.find((marker) => marker.getTitle() === activeStoreId);
            if (activeStore) {
                activeStore.trigger("click");
            }
        }
    }, [activeStoreId, markers]);
    return {
        isSearchVisible,
        mapRef,
        inputRef,
        isMapLoading,
        isMapError,
        isSearchResultPending,
        handleInitMap,
        handleInputSearchClick,
        handleLoadMarkers,
        handleLoadUserMarker,
    };
};
//# sourceMappingURL=useNaverMapsStoreLocator.js.map